import React, { useEffect, useState } from 'react';
import { useDeleteRule, useRules, useUpdateRule } from 'src/hooks/queries/rules';
import { useLocation } from 'react-router-dom';
import { getPriorities } from 'src/utils/rule.util';
import { DndContext, DragEndEvent, closestCenter, Announcements } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { RuleModel } from 'src/types/rule.model';
import Selectors from 'src/utils/selectors';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import styles from './dragAndDropRules.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';
import EmptyStateBox from 'src/components/empty-state-box/EmptyStateBox';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import DragRuleFormikWrapper from 'src/components/atomic/molecules/dragRule/DragRule';
import { useCategories } from 'src/hooks/queries/categories';
import { CategoryModel } from 'src/types/category.model';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import UtilsService from 'src/services/utils.service';

export default function DragAndDropRulesWrapper(): JSX.Element {
    const { pathname } = useLocation();
    const { isLoading: isLoadingRules, data: rules } = useRules();
    const { isLoading: isLoadingCategories, data: categories } = useCategories();
    const { isMinWidthMedium } = useBreakpoints();
    const { mutateAsync: updateRule } = useUpdateRule();
    const { mutateAsync: deleteRule } = useDeleteRule();
    const [ruleItems, setRuleItems] = useState<RuleModel[]>([]);
    const [rulePriorities, setRulePriorities] = useState<number[]>();
    const isLoading = isLoadingRules || isLoadingCategories;

    /** Sets the expense rules when they have loaded */
    useEffect(() => {
        if (!isLoadingRules) {
            setRuleItems(rules);
            setRulePriorities(getPriorities(rules));
        }
    }, [isLoadingRules, rules]);

    const handleSubmit = async (updatedRule: RuleModel): Promise<void> => {
        await updateRule(updatedRule);
        trackClick(pathname, adobeTags.Rules.Check);
    };

    const handleDelete = async (rule: RuleModel): Promise<void> => {
        await deleteRule(rule?.id);
        trackClick(pathname, adobeTags.Rules.Delete);
    };

    /** Handles dragging and dropping of the rules within the droppable context */
    const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const activeIndex = ruleItems.findIndex((rule) => rule.id === active.id);
            const overIndex = ruleItems.findIndex((rule) => rule.id === over.id);
            const newRuleItems = UtilsService.dragReorder(activeIndex, overIndex, ruleItems);
            setRuleItems(newRuleItems);
            await updateRule({
                ...ruleItems[activeIndex],
                priority: rulePriorities[overIndex]
            });
        }
    };

    return (
        <DragAndDropRules
            isLoading={isLoading}
            categories={categories}
            pathname={pathname}
            handleSubmit={handleSubmit}
            handleDelete={handleDelete}
            ruleItems={ruleItems}
            handleDragEnd={handleDragEnd}
            isMinWidthMedium={isMinWidthMedium}
        />
    );
}

export interface DragAndDropRulesProps {
    isLoading?: boolean;
    categories: CategoryModel[];
    pathname: string;
    ruleItems: RuleModel[];
    handleSubmit: (rule: RuleModel) => Promise<void>;
    handleDelete: (rule: RuleModel) => Promise<void>;
    handleDragEnd: (result: DragEndEvent) => Promise<void>;
    isMinWidthMedium?: boolean;
}
export function DragAndDropRules({
    isLoading,
    pathname,
    categories,
    ruleItems,
    handleSubmit,
    handleDelete,
    handleDragEnd,
    isMinWidthMedium
}: DragAndDropRulesProps): JSX.Element {
    const getRulePosition = (id: string): number => {
        return ruleItems.findIndex((rule) => rule.id === id);
    };

    // Accessibility announcements for drag and drop
    const accessibilityAnnouncements: Announcements = {
        onDragStart: (event: DragEndEvent) => {
            return `Picked up rule ${getRulePosition(String(event.active.id)) + 1}`;
        },
        onDragOver: (event: DragEndEvent) => {
            return `Moved rule ${getRulePosition(String(event.active.id)) + 1} to position ${
                getRulePosition(String(event.over.id)) + 1
            }`;
        },
        onDragEnd: (event: DragEndEvent) => {
            return `Dropped rule ${getRulePosition(String(event.active.id)) + 1} at position ${
                getRulePosition(String(event.over.id)) + 1
            }`;
        },
        onDragCancel: (event: DragEndEvent) => {
            return `Cancelled drag. Rule ${getRulePosition(String(event.active.id)) + 1} returned to original position`;
        }
    };

    return isLoading || !ruleItems ? (
        <div data-cy={Selectors.General.LOADING_INDICATOR} />
    ) : ruleItems.length > 0 ? (
        <DndContext
            accessibility={{ announcements: accessibilityAnnouncements }}
            collisionDetection={closestCenter}
            onDragEnd={(result) => {
                void handleDragEnd(result);
                trackClick(pathname, adobeTags.Rules.Drag);
            }}
        >
            <SortableContext items={ruleItems.map((rule) => rule.id)} id="sortableRules">
                <div className={styles.droppableTransactionsList} data-cy={Selectors.Transactions.DRAG_AND_DROP_RULES}>
                    {ruleItems.map((rule, index) => {
                        return (
                            <DragRuleFormikWrapper
                                rule={rule}
                                ruleItems={ruleItems}
                                pathname={pathname}
                                index={index}
                                key={index}
                                categories={categories}
                                handleSubmit={handleSubmit}
                                handleDelete={handleDelete}
                                isMinWidthMedium={isMinWidthMedium}
                            />
                        );
                    })}
                </div>
            </SortableContext>
        </DndContext>
    ) : (
        <EmptyStateBox className={styles.emptyStateBox}>
            <div aria-live="polite">
                <Typography
                    component="p"
                    variant={TypographyVariants.BODY_BOLD}
                    data-cy={Selectors.Transactions.RULE_EMPTY_STATE_DESC}
                >
                    {LiteralsService.getLiteral('DragAndDropRules.emptyState.noRules')}
                </Typography>
                <Typography component="p" variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('DragAndDropRules.emptyState.viewDetails')}
                </Typography>
            </div>
        </EmptyStateBox>
    );
}
